import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ImageIcon, Music, Clock, Calendar, Info, FileText, Tag, Settings, Mic, Loader2 } from 'lucide-react';
import type { PodcastWithRelations, Categories, Tags } from '../../lib/supabase-types';
import ImageWithDimensions from './ImageWithDimensions';
import { Modal, ModalFooter, ModalButton } from '../shared/Modal';
import { usePodcastTranscription } from '../../hooks/usePodcastTranscription';
import { toast } from 'react-hot-toast';
import TranscriptionLoadingModal from '../shared/TranscriptionLoadingModal';

interface PodcastEditModalProps {
  podcast: PodcastWithRelations | null;
  categories: Categories[];
  tags: Tags[];
  selectedTags: Tags[];
  onSave: () => Promise<void>;
  onClose: () => void;
  onChange: (podcast: PodcastWithRelations) => void;
  onTagsChange: (tags: Tags[]) => void;
  onImageBrowse: () => void;
  onAudioBrowse: () => void;
  isOpen: boolean;
  setActiveSection: (section: string) => void;
}

export const PodcastEditModal: React.FC<PodcastEditModalProps> = ({
  podcast,
  categories,
  tags,
  selectedTags,
  onSave,
  onClose,
  onChange,
  onTagsChange,
  onImageBrowse,
  onAudioBrowse,
  isOpen,
  setActiveSection
}) => {
  if (!podcast) return null;

  const [date, setDate] = useState(podcast.published_at ? podcast.published_at.split('T')[0] : '');
  const [time, setTime] = useState(podcast.published_at ? podcast.published_at.split('T')[1].slice(0, 5) : '');
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [transcription, setTranscription] = useState('');
  const [isGeneratingTranscription, setIsGeneratingTranscription] = useState(false);
  const { generateTranscription, fetchTranscription, checkTranscriptionValidity } = usePodcastTranscription();
  
  // Load existing transcription when the modal opens for an existing podcast
  useEffect(() => {
    const loadExistingTranscription = async () => {
      if (podcast?.id) {
        try {
          const transcriptionData = await fetchTranscription(podcast.id);
          setTranscription(transcriptionData.content);
        } catch (err) {
          console.log('No existing transcription found');
          // No need to show an error toast here as this is expected for new podcasts
        }
      }
    };
    
    loadExistingTranscription();
  }, [podcast?.id]);

  const tabs = [
    { id: 'info', label: 'Informações', icon: Info },
    { id: 'media', label: 'Media', icon: Music },
    { id: 'tags', label: 'Tags', icon: Tag },
    { id: 'transcription', label: 'Transcrição Áudio', icon: FileText },
    { id: 'settings', label: 'Configurações', icon: Settings }
  ];

  const handleGenerateTranscription = async () => {
    if (!podcast?.audio_url) {
      toast.error('URL do áudio não encontrada');
      return;
    }

    console.log('Checking for existing transcription for:', podcast.id);
    try {
      // Don't set loading state yet, only after user confirms
      
      // First check if a valid transcription exists
      const validityCheck = await checkTranscriptionValidity(podcast.id);
      
      if (validityCheck.isValid && validityCheck.transcription) {
        // If valid transcription exists, load it
        console.log('Valid transcription found, loading content');
        setTranscription(validityCheck.transcription.content);
        toast.success('Transcrição carregada com sucesso!');
        setIsGeneratingTranscription(false);
        return;
      }
      
      // If transcription is processing, inform the user and start polling
      if (!validityCheck.isValid && validityCheck.message?.includes('processada')) {
        const toastId = toast.loading(validityCheck.message);
        // Set loading state for polling process
        setIsGeneratingTranscription(true);
        
        // Start polling for transcription status
        const pollTranscriptionStatus = async () => {
          try {
            const updatedCheck = await checkTranscriptionValidity(podcast.id);
            
            if (updatedCheck.isValid && updatedCheck.transcription) {
              // Transcription completed successfully
              toast.dismiss(toastId);
              toast.success('Transcrição carregada com sucesso!');
              setTranscription(updatedCheck.transcription.content);
              setIsGeneratingTranscription(false);
              return;
            } else if (updatedCheck.transcription?.status === 'failed') {
              // Transcription failed
              toast.dismiss(toastId);
              toast.error(`Falha na transcrição: ${updatedCheck.transcription.error || 'Erro desconhecido'}`);
              setIsGeneratingTranscription(false);
              return;
            } else if (updatedCheck.transcription?.status === 'processing') {
              // Still processing, continue polling
              setTimeout(pollTranscriptionStatus, 5000); // Check again in 5 seconds
            } else {
              // Unknown status
              toast.dismiss(toastId);
              toast.error('Status da transcrição desconhecido');
              setIsGeneratingTranscription(false);
            }
          } catch (error) {
            console.error('Error polling transcription status:', error);
            toast.dismiss(toastId);
            toast.error('Erro ao verificar o status da transcrição');
            setIsGeneratingTranscription(false);
          }
        };
        
        // Start polling after a short delay
        setTimeout(pollTranscriptionStatus, 5000);
        return;
      }
      
      // If transcription failed or doesn't exist, ask user if they want to generate a new one
      if (!validityCheck.isValid) {
        const message = validityCheck.message || 'Nenhuma transcrição válida encontrada.';
        const confirmGenerate = window.confirm(
          `${message}\n\nDeseja utilizar o SinopseIA para gerar uma nova transcrição?`
        );
        
        if (!confirmGenerate) {
          return;
        }
        // Only set loading state after user confirms generation
        setIsGeneratingTranscription(true);
      }

      // Generate new transcription
      console.log('Starting transcription generation for:', podcast.audio_url);
      const result = await generateTranscription(podcast.id, podcast.audio_url);
      console.log('Transcription result:', result);
      setTranscription(result.content);
      toast.success('Transcrição gerada com sucesso!');
    } catch (error) {
      console.error('Error handling transcription:', error); 
      toast.error('Erro ao gerar a transcrição');
    } finally {
      setIsGeneratingTranscription(false);
    }
  };

  return (
    <>
      <TranscriptionLoadingModal isOpen={isGeneratingTranscription} />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={podcast.id ? 'Editar Podcast' : 'Adicionar Podcast'}
        maxWidth="4xl"
      >
      <div className="p-6">
        {/* Tabs Navigation */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Título</label>
                <input
                  type="text"
                  value={podcast.title}
                  onChange={(e) => onChange({ ...podcast, title: e.target.value })}
                  className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descrição</label>
                <textarea
                  value={podcast.description || ''}
                  onChange={(e) => onChange({ ...podcast, description: e.target.value })}
                  rows={8}
                  className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Categoria</label>
                <select
                  value={podcast.category_id || ''}
                  onChange={(e) => onChange({ ...podcast, category_id: e.target.value })}
                  className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">URL da Imagem</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={podcast.image_url || ''}
                    onChange={(e) => onChange({ ...podcast, image_url: e.target.value })}
                    className="flex-1 px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                  <button
                    onClick={onImageBrowse}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <ImageIcon className="w-5 h-5" />
                  </button>
                </div>
                {podcast.image_url && (
                  <div className="mt-2">
                    <ImageWithDimensions src={podcast.image_url} alt="Preview" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">URL do Áudio</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={podcast.audio_url || ''}
                    onChange={(e) => onChange({ ...podcast, audio_url: e.target.value })}
                    className="flex-1 px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                  <button
                    onClick={onAudioBrowse}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Music className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tags Tab */}
          {activeTab === 'tags' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Tags</label>
                <div className="flex flex-wrap gap-2 p-4 bg-secondary/30 rounded-lg min-h-[100px]">
                  {tags.map((tag) => {
                    const isSelected = selectedTags.some(t => t.id === tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => onTagsChange(
                          isSelected
                            ? selectedTags.filter(t => t.id !== tag.id)
                            : [...selectedTags, tag]
                        )}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          isSelected
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary hover:bg-secondary/80'
                        }`}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Transcription Tab */}
          {activeTab === 'transcription' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Transcrição do Áudio</h3>
                <button
                  onClick={handleGenerateTranscription}
                  disabled={isGeneratingTranscription || !podcast.audio_url}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {isGeneratingTranscription ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      A Gerar...
                    </>
                  ) : (
                    <>
                      <Mic className="w-5 h-5" />
                      Gerar Transcrição
                    </>
                  )}
                </button>
              </div>

              <textarea
                value={transcription}
                onChange={(e) => setTranscription(e.target.value)}
                className="w-full h-[400px] px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none font-mono text-sm"
                placeholder={isGeneratingTranscription ? 'A Gerar a transcrição...' : 'A transcrição vai aparecer aqui...'}
                readOnly={isGeneratingTranscription}
              />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Autor</label>
                <input
                  type="text"
                  value={podcast.author || ''}
                  onChange={(e) => onChange({ ...podcast, author: e.target.value })}
                  className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Duração</label>
                <input
                  type="text"
                  value={podcast.duration || ''}
                  onChange={(e) => onChange({ ...podcast, duration: e.target.value })}
                  className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  placeholder="00:00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Data de Publicação</label>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => {
                      setDate(e.target.value);
                      const newDate = new Date(`${e.target.value}T${time || '00:00'}`);
                      onChange({ ...podcast, published_at: newDate.toISOString() });
                    }}
                    className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                  <input
                    type="time"
                    value={time}
                    onChange={(e) => {
                      setTime(e.target.value);
                      const newDate = new Date(`${date || new Date().toISOString().split('T')[0]}T${e.target.value}`);
                      onChange({ ...podcast, published_at: newDate.toISOString() });
                    }}
                    className="w-full px-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ModalFooter>
        <ModalButton onClick={onClose}>Cancelar</ModalButton>
        <ModalButton onClick={onSave} variant="primary">
          {isSaving ? 'A Guardar...' : podcast.id ? 'Guardar' : 'Criar'}
        </ModalButton>
      </ModalFooter>
    </Modal>
    </>
  );
};